// HelpDeskDashboard.js - Main Dashboard for Help Desk Module

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Card, CardContent, CardActions,
  Button, Chip, Avatar, Divider, CircularProgress, Alert, Stack
} from '@mui/material';
import {
  ConfirmationNumber as TicketIcon,
  MenuBook as KnowledgeIcon,
  AccessTime as SLAIcon,
  SupportAgent as HelpDeskIcon,
  Add as AddIcon,
  ArrowForward as ArrowForwardIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import { hasPermission } from '../../../utils/permissionUtils';
import { ticketService } from './helpDeskService';
import {
  TicketStatusLabels, TicketStatusColors,
  TicketPriorityLabels, TicketPriorityColors
} from './helpDeskEnums';

const HelpDeskDashboard = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);

  useEffect(() => {
    document.title = 'Help Desk | KlonIT CRM';
    checkPermissions();
  }, []);

  useEffect(() => {
    if (canView) {
      loadDashboard();
    }
  }, [canView, chatbotId]);

  const checkPermissions = async () => {
    try {
      const viewPerm = await hasPermission('crm', 'view');
      const modifyPerm = await hasPermission('crm', 'add|edit');
      setCanView(viewPerm);
      setCanModify(modifyPerm);
      if (!viewPerm) {
        setLoading(false);
      }
    } catch (err) {
      console.error('Error checking permissions:', err);
      setLoading(false);
    }
  };

  const loadDashboard = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = chatbotId ? { chatbot_id: chatbotId } : {};
      const statsRes = await ticketService.getStats(params);

      if (statsRes.success) {
        setStats(statsRes.data);
      }
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canView) {
    return (
      <Alert severity="error" sx={{ mt: 10, mx: 3 }}>
        <strong>Access Denied</strong><br />
        You don't have permission to view this module.
      </Alert>
    );
  }

  const cards = [
    {
      title: 'Support Tickets',
      description: 'Manage customer support cases with priority levels, SLA tracking, and status workflows.',
      icon: <TicketIcon sx={{ fontSize: 60, color: '#1976d2' }} />,
      count: stats?.open_tickets || 0,
      link: `/chatbot/${chatbotId}/crm/helpdesk/tickets`,
      color: '#1976d2'
    },
    {
      title: 'Ticket Categories',
      description: 'Organize tickets by creating and managing categories and subcategories.',
      icon: <CategoryIcon sx={{ fontSize: 60, color: '#9c27b0' }} />,
      count: stats?.categories_count || 0,
      link: `/chatbot/${chatbotId}/crm/helpdesk/ticket-categories`,
      color: '#9c27b0'
    },
    {
      title: 'Knowledge Base',
      description: 'Create and manage help articles, FAQs, and documentation for customers and agents.',
      icon: <KnowledgeIcon sx={{ fontSize: 60, color: '#2e7d32' }} />,
      count: stats?.articles_count || 0,
      link: `/chatbot/${chatbotId}/crm/helpdesk/knowledge-base`,
      color: '#2e7d32'
    },
    {
      title: 'SLA Policies',
      description: 'Configure service level agreements with response and resolution time targets.',
      icon: <SLAIcon sx={{ fontSize: 60, color: '#ed6c02' }} />,
      count: stats?.sla_policies_count || 0,
      link: `/chatbot/${chatbotId}/crm/helpdesk/sla-policies`,
      color: '#ed6c02'
    }
  ];

  return (
    <Box sx={{ p: 3, mt: 10 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            <HelpDeskIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Help Desk
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Customer Service & Support
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Main Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {cards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: '0.3s',
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-4px)'
                }
              }}
              onClick={() => navigate(card.link)}
            >
              <CardContent sx={{ flexGrow: 1, textAlign: 'center', pt: 4 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: `${card.color}15`,
                    margin: '0 auto 16px'
                  }}
                >
                  {card.icon}
                </Avatar>
                <Typography variant="h5" gutterBottom fontWeight={600}>
                  {card.title}
                </Typography>
                <Typography variant="h3" color={card.color} fontWeight={700} sx={{ mb: 2 }}>
                  {card.count.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {card.description}
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ bgcolor: card.color }}
                >
                  Manage
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Ticket Stats and Priority Breakdown */}
      <Grid container spacing={3}>
        {/* Ticket Pipeline */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Ticket Pipeline
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              By Status
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 3 }}>
              {stats?.status_breakdown && Object.entries(stats.status_breakdown).map(([status, count]) => (
                <Chip
                  key={status}
                  label={`${TicketStatusLabels[status] || status}: ${count}`}
                  color={TicketStatusColors[status] || 'default'}
                  size="small"
                  sx={{ mb: 1 }}
                />
              ))}
              {(!stats?.status_breakdown || Object.keys(stats.status_breakdown).length === 0) && (
                <Typography variant="body2" color="text.secondary">
                  No ticket data available
                </Typography>
              )}
            </Stack>

            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              By Priority
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {stats?.priority_breakdown && Object.entries(stats.priority_breakdown).map(([priority, count]) => (
                <Chip
                  key={priority}
                  label={`${TicketPriorityLabels[priority] || priority}: ${count}`}
                  color={TicketPriorityColors[priority] || 'default'}
                  variant="outlined"
                  size="small"
                  sx={{ mb: 1 }}
                />
              ))}
              {(!stats?.priority_breakdown || Object.keys(stats.priority_breakdown).length === 0) && (
                <Typography variant="body2" color="text.secondary">
                  No priority data available
                </Typography>
              )}
            </Stack>

            {canModify && (
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => navigate(`/chatbot/${chatbotId}/crm/helpdesk/tickets?new=true`)}
                >
                  Create New Ticket
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Performance Summary */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Performance Summary
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Stack spacing={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Open Tickets
                </Typography>
                <Typography variant="h6" fontWeight={600} color="primary.main">
                  {stats?.open_tickets || 0}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Overdue Tickets
                </Typography>
                <Typography variant="h6" fontWeight={600} color="error.main">
                  {stats?.overdue_tickets || 0}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  SLA Compliance
                </Typography>
                <Typography variant="h6" fontWeight={600} color="success.main">
                  {stats?.sla_compliance || 100}%
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Resolved This Month
                </Typography>
                <Typography variant="h6" fontWeight={600} color="success.main">
                  {stats?.resolved_month || 0}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      {canModify && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom fontWeight={600}>
            Quick Actions
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              startIcon={<TicketIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/crm/helpdesk/tickets?new=true`)}
            >
              New Ticket
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<CategoryIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/crm/helpdesk/ticket-categories`)}
            >
              Manage Categories
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<KnowledgeIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/crm/helpdesk/knowledge-base?new=true`)}
            >
              New Article
            </Button>
            <Button
              variant="contained"
              color="warning"
              startIcon={<SLAIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/crm/helpdesk/sla-policies`)}
            >
              Manage SLA
            </Button>
            <Button
              variant="outlined"
              startIcon={<ArrowForwardIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/crm/helpdesk/tickets`)}
            >
              View All Tickets
            </Button>
          </Stack>
        </Paper>
      )}
    </Box>
  );
};

export default HelpDeskDashboard;
